// @ts-check
// Prévia do e-mail (spec/email.md seção 19.2): iframe com sandbox="" (sem scripts nem formulários) e srcdoc; "Computador"
// (680 px), "Celular" (375 px, com moldura) e "Texto". O diálogo de prévia escolhe "Ver com os dados de": os dados de
// exemplo (renderizador no navegador) ou um card buscado pelo nome (POST /rest/email-templates/preview, com o papel de
// quem pede: campo que esse papel não lê sai vazio, com o aviso).
import { html } from 'htm/preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { previewEmailDocument } from '../../data/email-templates.js';
import { searchRecords } from '../../data/rest.js';
import { Button } from '../../ui/button.js';
import { Icon } from '../../ui/icon.js';
import { Dropdown, DropdownMenuItems, DropdownMenuSearchInput, MenuItem } from '../../ui/dropdown.js';
import { useDebouncedValue, useIsMobile } from '../../ui/hooks.js';
import { showErrorSnackbar } from '../../ui/snackbar.js';
import { SandboxedHtmlFrame } from './sandboxed-html-frame.js';
import { EmailDialog } from './email-dialog.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {'DESKTOP' | 'MOBILE' | 'TEXT'} PreviewDevice
 * @typedef {{ html: string, text: string, subject: string, preheader: string, estimatedHeight?: number }} PreviewMessage
 */

export const PREVIEW_WIDTHS = Object.freeze({ DESKTOP: 680, MOBILE: 375 });

/**
 * Alternância Computador, Celular e Texto.
 * @param {{ device: PreviewDevice, onChange: (device: PreviewDevice) => void }} props
 */
export function PreviewDeviceSwitch({ device, onChange }) {
  const options = /** @type {const} */ ([
    { value: 'DESKTOP', icon: 'IconDeviceDesktop', key: 'desktop' },
    { value: 'MOBILE', icon: 'IconDeviceMobile', key: 'mobile' },
    { value: 'TEXT', icon: 'IconFileText', key: 'text' },
  ]);
  return html`<div class="email-device-switch" role="radiogroup" aria-label=${t('emailTemplates.preview.title')}>
    ${options.map((option) => html`<button type="button" role="radio" class="email-choice-option" aria-checked=${device === option.value ? 'true' : 'false'}
      onClick=${() => onChange(option.value)}>
      <${Icon} name=${option.icon} size=${14} /><span>${t(`emailTemplates.preview.${option.key}`)}</span>
    </button>`)}
  </div>`;
}

/**
 * @param {{ message: PreviewMessage, device: PreviewDevice, senderName?: string }} props
 */
export function EmailPreviewFrame({ message, device, senderName }) {
  const height = Math.min(4000, Math.max(560, (message.estimatedHeight ?? 800) + 220));
  return html`<div class="email-preview" data-device=${device}>
    <div class="email-preview-envelope">
      ${senderName ? html`<span class="email-preview-sender">${senderName}</span>` : null}
      <span class="email-preview-subject">${message.subject || t('emailTemplates.preview.noSubject')}</span>
      ${message.preheader ? html`<span class="email-preview-preheader">${message.preheader}</span>` : null}
    </div>
    ${device === 'TEXT'
      ? html`<pre class="email-preview-text">${message.text}</pre>`
      : device === 'MOBILE'
        ? html`<div class="email-phone-frame">
            <${SandboxedHtmlFrame} html=${message.html} title=${t('emailTemplates.preview.mobile')} width=${PREVIEW_WIDTHS.MOBILE} height=${Math.min(height * 1.4, 4000)} />
          </div>`
        : html`<${SandboxedHtmlFrame} html=${message.html} title=${t('emailTemplates.preview.desktop')} width=${PREVIEW_WIDTHS.DESKTOP} height=${height} className="email-preview-desktop" />`}
  </div>`;
}

/**
 * Seletor "Ver com os dados de": exemplo ou um card (busca pelo nome, só os que o papel vê).
 * @param {{ value: { id: string, label: string } | null, onChange: (card: { id: string, label: string } | null) => void }} props
 */
export function SampleCardPicker({ value, onChange }) {
  const anchorRef = useRef(/** @type {HTMLElement | null} */ (null));
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debounced = useDebouncedValue(search, 250);
  const [results, setResults] = useState(/** @type {Array<{ recordId: string, label: string }>} */ ([]));
  useEffect(() => {
    if (!isOpen) return undefined;
    let isCancelled = false;
    searchRecords(debounced, { limit: 8, includedObjectNameSingulars: ['enrollmentOpportunity'] })
      .then((items) => {
        if (!isCancelled) setResults(items.filter((item) => item.objectNameSingular === 'enrollmentOpportunity'));
      })
      .catch(() => !isCancelled && setResults([]));
    return () => {
      isCancelled = true;
    };
  }, [debounced, isOpen]);
  return html`<div class="email-sample-picker">
    <span class="email-sample-label">${t('emailTemplates.preview.sampleData')}</span>
    <span ref=${anchorRef}>
      <${Button} title=${value ? value.label : t('emailTemplates.preview.sampleExample')} icon="IconUser" size="small" variant="secondary"
        onClick=${() => setIsOpen((open) => !open)} />
    </span>
    <${Dropdown} anchorRef=${anchorRef} isOpen=${isOpen} onClose=${() => setIsOpen(false)} width=${280}>
      <${DropdownMenuSearchInput} value=${search} onChange=${setSearch} placeholder=${t('emailTemplates.preview.sampleCard')} />
      <${DropdownMenuItems} hasMaxHeight=${true}>
        <${MenuItem} text=${t('emailTemplates.preview.sampleExample')} icon="IconSparkles" selected=${!value} onClick=${() => { onChange(null); setIsOpen(false); }} />
        ${results.map((item) => html`<${MenuItem} key=${item.recordId} text=${item.label} icon="IconTargetArrow" selected=${value?.id === item.recordId}
          onClick=${() => { onChange({ id: item.recordId, label: item.label }); setIsOpen(false); }} />`)}
      <//>
    <//>
  </div>`;
}

/**
 * Diálogo "Prévia".
 * @param {{ onClose: () => void, local: PreviewMessage, senderName?: string,
 *   request: { document: import('../../data/email-templates.js').EmailDocument, subject: string, preheader: string, purpose: 'MARKETING' | 'SERVICE' } }} props
 */
export function EmailPreviewDialog({ onClose, local, request, senderName }) {
  const isMobile = useIsMobile();
  const [device, setDevice] = useState(/** @type {PreviewDevice} */ (isMobile ? 'MOBILE' : 'DESKTOP'));
  const [card, setCard] = useState(/** @type {{ id: string, label: string } | null} */ (null));
  const [remote, setRemote] = useState(/** @type {(PreviewMessage & { warnings: Array<{ code: string, path?: string }> }) | null} */ (null));
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!card) {
      setRemote(null);
      return undefined;
    }
    let isCancelled = false;
    setIsLoading(true);
    previewEmailDocument({ ...request, sampleCardId: card.id })
      .then((preview) => {
        if (!isCancelled) setRemote(preview);
      })
      .catch((error) => {
        if (isCancelled) return;
        showErrorSnackbar(error);
        setCard(null);
      })
      .finally(() => !isCancelled && setIsLoading(false));
    return () => {
      isCancelled = true;
    };
  }, [card?.id]);

  const message = card && remote ? remote : local;
  const hidden = (remote?.warnings ?? []).filter((warning) => warning.code === 'VARIABLE_HIDDEN' || warning.code === 'UNKNOWN_VARIABLE');
  return html`<${EmailDialog} title=${t('emailTemplates.preview.title')} onClose=${onClose} size="full" className="email-preview-dialog" testId="email-preview-dialog"
    headerActions=${html`<${PreviewDeviceSwitch} device=${device} onChange=${setDevice} />`}>
    <div class="email-preview-controls">
      <${SampleCardPicker} value=${card} onChange=${setCard} />
      ${isLoading ? html`<span class="email-preview-loading">${t('emailTemplates.loading')}</span>` : null}
    </div>
    ${hidden.length > 0
      ? html`<div class="email-inline-warning" role="status">
          <${Icon} name="IconEyeOff" size=${14} />
          <span>${t('emailTemplates.preview.hiddenVariables', { list: hidden.map((warning) => warning.path).filter(Boolean).join(', ') })}</span>
        </div>`
      : null}
    <${EmailPreviewFrame} message=${message} device=${device} senderName=${senderName} />
  <//>`;
}
